import sha1 from 'js-sha1'
import { range, sum } from 'lodash-es'
import tinycolor from 'tinycolor2'

/**
 * Roughly identifies if the color is way too dark and needs white text instead of black.
 * Not using {@link tinycolor.readability()} because it seems a bit lazy, and the second color is fixed anyway.
 * @see https://stackoverflow.com/a/9733420/102960
 * @param color {string} Any color that {@link tinycolor} can interpret - RGB(A), HSL(A), HSV(A) as either string or object (e.g. {r,g,b,a})
 */
export const tooDark = color => {
	const lum = tinycolor(color).getLuminance()
	return ((lum + 0.05) / 0.05) < 4.5 //4.5 is AAA for large text and AA for normal; second value is black (0 luminance)
}

/**
 * Tries to identify if the color is so near our "danger" tone that it's better to use orange instead.
 * @param color {string} Any color that {@link tinycolor} can interpret - RGB(A), HSL(A), HSV(A) as either string or object (e.g. {r,g,b,a})
 * @see https://i7x7p5b7.stackpathcdn.com/codrops/wp-content/uploads/2015/01/hsl-color-wheel.png
 */
export const isReddish = color => {
	const hue = tinycolor(color).toHsl().h
	return hue <= 15 || hue >= 345
}

/**
 * Contains a "simple" list of colors which are easy to distinguish between any of them, at least to some extent.
 * Given we ended up with 10 colors, they're not THAAAAT easy to tell apart in some scenarios, and thus there's a shorter list with only 5, that should cover 99.9% of clients. The list should be selected with {@link select} once we know how many colors we need. If that's not done, the long list is the default.
 * All of this is to make real the use of {@link fromText}.
 *
 * Long, boring explanation on how this list came to be, and how to reproduce/edit it:
 * First, a statistical query was run to identify the "necessary" number of colors here. It was found out that only two accounts have more than 10 locations, with 3 having 6~7 locs and 99.91% have up to 5 locations. Thus, it seems safe to select 10 colors.
 * Those colors were initially selected from https://thobl.github.io/LCh-Lab-color-palette/, using almost average values for LCh variation, getting an almost even distribution of hues and saturations(?).
 * Lastly, they were thrown into AccessiblePalette.com (https://is.gd/HmpaFh), shifting some hues that were way similar, and selecting the lightness/contrast value which matches WCAG 2.1 AAA's ratio of 7 - line 700, value of 36 (on the first column).
 */
export class DistinguishableColors {
	static LIST = ['AC0038', '9D2C00', '6B5100', '1E6200', '00653B', '005E64', '005A91', '384EA5', '5241B7', '9B1878']
			       .map(rgb => ({ rgb, angle: Math.round(tinycolor(rgb).toHsl().h) }))

	static #groupFor(length) {
		const colorsTotal = DistinguishableColors.LIST.length
		const step = colorsTotal / length
		return range(0, Math.ceil(step))
				.map(start => (  //Each group starts in progressively higher keys, up to the max we need (steps/skips)...
					range(start, colorsTotal, step) //...skipping given the step size...
						.map(Math.floor)  //...and rounding down, so we stay inside the maximum number of colors
						.map(key => `#${DistinguishableColors.LIST[key].rgb}`)
				))
	}

	//TODO one way to improve the groups, reducing the number of similar colors, is breaking the luminance rule and changing it when neighboring colors start to happen
	static selectGroup(length, seed) {
		let groups = DistinguishableColors.#groupFor(length)
		if (!groups) { //failsafe #1 - TODO check user logs after a while before removing this
			console.warn('could not group colors', { length, seed })
			groups = DistinguishableColors.LIST
		}

		while (seed.length > 10) { //reduce it to a single digit
			seed = sum(seed.split(''))
			if (seed == 10) {
				seed = 0
			}
		}

		while (seed > groups.length-1) { //then, if it's still higher than the number of groups, we reduce it even more
			seed -= groups.length
		}

		if (groups[seed] == undefined) { //failsafe #2 - TODO check user logs after a while before removing this
			return groups[0]
		} else {
			return groups[seed]
		}
	}

	/**
	 * Chooses a color from a list, exclusive to the given string.
	 * Based on a curious algorithm from XMPP which Joel found, removing things like color-blindness correction.
	 *
	 * **DEPRECATED**: Solely, this is unreliable as it may return repeated colors for different strings, so it's better to use {@link selectGroup()} to distribute colors to a list of... things.
	 * @deprecated
	 * @see https://xmpp.org/extensions/xep-0392.html#algorithm
	 * @see https://matthewwild.co.uk/projects/xep0392-web/
	 * @param text {string|*} Anything else will be coerced into string ¯\_(ツ)_/¯
	 * @return {string} hexadecimal color
	 */
	static fromText(text) {
		const digest = sha1.digest(String(text)) //yep, needs to be cast as string, otherwise it fails
		const angle = (((digest[0] & 0xFF) + ((digest[1]) & 0xFF) * 256) / 65536.0) * 360;
		DistinguishableColors.LIST.sort((a, b) => {
			const distanceA = Math.min(modulo((angle - a.angle), 360), modulo((a.angle - angle), 360))
			const distanceB = Math.min(modulo((angle - b.angle), 360), modulo((b.angle - angle), 360))
			return distanceA - distanceB
		})
		// console.debug(`Selected color for ${text}`, { digest, angle, color: DistinguishableColors.selected[0] })
		return `#${DistinguishableColors.LIST[0].rgb}`
	}
}

/**
 * Modulo operation, different from the plain Remainder operation. See note at https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Remainder
 * @param a {number}
 * @param b {number}
 * @return {number}
 */
const modulo = (a, b) => ((a % b) + b) % b
